import { Fragment, FunctionalComponent, h } from "preact";
import { Route, Router } from "preact-router";
import NotFoundPage from "../routes/notfound";
import Header from "./header";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Lookup from "../routes/lookup";
import Login from "../routes/login";
import NoPermissions from "../routes/nopermissions";
import { useEffect, useState } from "preact/hooks";
import { clearTokens, initializeTokenManager, getAuthorization, getTokenGrants } from "../services/token-service";
import { applyServiceWorkerUpdate, watchForServiceWorkerUpdate } from "../services/sw-update";
import { GrantsContext } from "../contexts/grants-context";
import InstallerTools from "../routes/installer-tools";
import SupportTools from "../routes/support-tools";

const App: FunctionalComponent = () => {
    const [grants, setGrants] = useState<string[] | undefined>(getTokenGrants());

    initializeTokenManager(setGrants);

    // Without this a tab left open across a deploy keeps silently running the old
    // bundle -- it looks like a bug in whatever feature just shipped.
    useEffect(() => {
        watchForServiceWorkerUpdate(() =>
            toast.info("A new version is available — click here to reload.", {
                autoClose: false,
                closeOnClick: false, // the reload is the close; don't dismiss on a stray click
                toastId: "sw-update", // survives repeated update checks without stacking
                onClick: applyServiceWorkerUpdate,
            })
        );
    }, []);

    const logout = () => {
        clearTokens();
        setGrants(undefined);
    };

    const login = async (email?: string, password?: string) => {
        const emailRegex =
            /^[a-zA-Z0-9.!#$%&'*+\/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;

        if (!email) {
            toast("Email is required", { type: "error" });
            return;
        }
        if (!emailRegex.test(email)) {
            toast("Email is invalid", { type: "error" });
            return;
        }

        if (!password) {
            toast("Password is required", { type: "error" });
            return;
        }
        if (password.length < 6) {
            toast("Password must be at least 6 characters", { type: "error" });
            return;
        }

        if (!(await getAuthorization(email, password))) {
            toast("Login failed", { type: "error" });
            return;
        }

        setGrants(getTokenGrants());
    };

    const getInnerContent = () => {
        if (!grants) {
            return (
                <>
                    <Login login={login} />;
                    <ToastContainer position="top-right" autoClose={4000} pauseOnHover theme="dark" />
                </>
            );
        }

        return (
            <>
                <Header logout={logout} />
                <div class="overflow-y-auto flex-1 pb-12">
                    {grants.length === 0 ? (
                        <NoPermissions />
                    ) : (
                        <GrantsContext.Provider value={grants}>
                            <Router>
                                <Route path="/" component={Lookup} />
                                <Route path="/lookup" component={Lookup} />
                                <Route path="/installer-tools" component={InstallerTools} />
                                <Route path="/support-tools" component={SupportTools} />
                                <NotFoundPage default />
                            </Router>
                        </GrantsContext.Provider>
                    )}
                </div>
            </>
        );
    };

    return (
        <div id="preact_root" class="bg-gray-800 overflow-y-hidden flex flex-col h-screen">
            {getInnerContent()}
            <ToastContainer position="top-right" autoClose={4000} pauseOnHover theme="dark" />
        </div>
    );
};

export default App;
